import { useTranslation } from 'react-i18next'

import { Text } from '@/components/ui/Text'
import { QuestionCard } from '../views/analysis/QuestionCard'
import { findSurveyQuestion } from '@/features/studies/report/utils/findSurveyQuestion'
import {
  normalizeQuestionAssetMentions,
  useReportQuestionCards,
} from '../views/overview/useReportQuestionCards'
import { resolvePairedQuestion } from '../views/overview/resolvePairedQuestion'
import { readTestedAssets } from '@/features/studies/report/utils/testedAssets'

import { CanonicalAnalysisOutput, QuestionPayload } from '@digital-twins/shared-types'
import { SegmentationMapping } from '@/types/components/audiences/segmentation-mapping'

interface FeaturedQuestionContext {
  survey?: any
  responses?: Array<any>
  analysisColumns?: Array<string>
  segmentationMappings?: Array<SegmentationMapping> | null
  canonicalAnalysis?: CanonicalAnalysisOutput | null
}

interface FeaturedQuestionViewProps {
  payload: QuestionPayload
  context: FeaturedQuestionContext
}

/**
 * What a question card says when its question is not in the compiled survey.
 *
 * Both question shapes (`question` and `fallback`) used to return `null` here,
 * which left an available card as a slot name over nothing: a reader cannot
 * tell that from a card still loading. The frame's own sentence for a read it
 * cannot show is the honest answer, and it is the same one in every locale
 * because it is a system sentence, not recipe copy.
 */
export function UnresolvedQuestionNotice() {
  const { t } = useTranslation('app')

  return (
    <div
      data-testid="summary-card-question-unresolved"
      className="flex-1 flex items-center justify-center text-sm text-muted-foreground text-center px-2 py-8"
    >
      <Text size="sm" color="muted">
        {t('studyReport.slotAbsent')}
      </Text>
    </div>
  )
}

export function FeaturedQuestionView(props: FeaturedQuestionViewProps) {
  const { payload, context } = props
  const { validResponses, segmentableQuestions, assetMentionDisplayMap } = useReportQuestionCards({
    survey: context.survey,
    responses: context.responses,
    segmentationMappings: context.segmentationMappings,
    monadicLaneScope: context.canonicalAnalysis?.monadicLaneScope,
  })
  const rawQuestion = findSurveyQuestion<unknown>(context.survey, payload.questionId)
  if (!rawQuestion) {
    return <UnresolvedQuestionNotice />
  }

  const questionMap = context.survey?._canonicalConfig?.questionMap
  const question = normalizeQuestionAssetMentions(rawQuestion, assetMentionDisplayMap)
  const pairedQuestion = resolvePairedQuestion(
    context.survey,
    questionMap,
    payload.questionId,
    payload.pairedQuestionId,
  )

  return (
    <div data-testid="summary-featured-question">
      <QuestionCard
        question={question}
        pairedQuestion={pairedQuestion}
        segmentableQuestions={segmentableQuestions}
        responses={validResponses}
        populationAttributes={context.analysisColumns}
        canonicalAnalysis={context.canonicalAnalysis}
        canonicalQuestionMap={questionMap}
        assetContextItems={context.survey?._canonicalConfig?.assetContext?.items}
        testedAssets={readTestedAssets(context.survey)}
        layout="stacked"
        segmentationMappings={context.segmentationMappings}
      />
    </div>
  )
}
